import { supabase } from '../lib/supabase';

export type SubscriptionPlan = 'free' | 'premium' | 'pro';
export type SubscriptionStatus = 'active' | 'cancelled' | 'expired' | 'trial';

export interface SubscriptionPlanDetails {
  id: string;
  name: string;
  display_name: string;
  price_monthly: number | null;
  price_yearly: number | null;
  features: Record<string, boolean>;
  limits: {
    ai_quiz_per_month: number;
    ai_instant_win_per_month: number;
    ai_game_contest_per_month: number;
    max_active_contests: number;
    max_users: number;
    max_projects: number;
  };
  sort_order: number;
}

export interface ProjectAddons {
  has_addons: boolean;
  id?: string;
  quantity: number;
  price_per_slot: number;
  monthly_cost: number;
  stripe_subscription_item_id?: string;
  created_at?: string;
}

export interface TenantSubscription {
  subscription_plan: SubscriptionPlan;
  subscription_status: SubscriptionStatus;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  trial_end_date: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export interface SubscriptionUsage {
  tenant_id: string;
  month: string;
  ai_quiz_generated: number;
  ai_instant_win_generated: number;
  ai_game_contest_generated: number;
  plan: SubscriptionPlan;
}

export const subscriptionService = {
  async getAllPlans(): Promise<SubscriptionPlanDetails[]> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    if (error) {
      console.error('Error fetching subscription plans:', error);
      throw new Error('Failed to fetch subscription plans');
    }

    return data || [];
  },

  async getPlanById(planId: string): Promise<SubscriptionPlanDetails | null> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('id', planId)
      .single();

    if (error) {
      console.error('Error fetching plan:', error);
      return null;
    }

    return data;
  },

  async getTenantSubscription(tenantId: string): Promise<TenantSubscription | null> {
    const { data, error } = await supabase
      .from('tenants')
      .select('subscription_plan, subscription_status, subscription_start_date, subscription_end_date, trial_end_date, stripe_customer_id, stripe_subscription_id')
      .eq('id', tenantId)
      .single();

    if (error) {
      console.error('Error fetching tenant subscription:', error);
      return null;
    }

    return data;
  },

  async getCurrentUsage(tenantId: string): Promise<SubscriptionUsage | null> {
    try {
      const { data, error } = await supabase.rpc('get_current_usage', {
        p_tenant_id: tenantId
      });

      if (error) {
        console.error('Error fetching current usage:', error);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Error calling get_current_usage:', err);
      return null;
    }
  },

  async checkAIQuota(tenantId: string, featureType: 'ai_quiz' | 'ai_instant_win' | 'ai_game_contest'): Promise<{
    allowed: boolean;
    plan: SubscriptionPlan;
    limit: number;
    used: number;
    remaining: number;
    reason?: string;
  }> {
    try {
      const { data, error } = await supabase.rpc('check_ai_quota', {
        p_tenant_id: tenantId,
        p_feature_type: featureType
      });

      if (error) {
        console.error('Error checking AI quota:', error);
        return {
          allowed: false,
          plan: 'free',
          limit: 0,
          used: 0,
          remaining: 0,
          reason: 'Failed to check quota'
        };
      }

      return data;
    } catch (err) {
      console.error('Error calling check_ai_quota:', err);
      return {
        allowed: false,
        plan: 'free',
        limit: 0,
        used: 0,
        remaining: 0,
        reason: 'Failed to check quota'
      };
    }
  },

  async incrementUsage(tenantId: string, featureType: 'ai_quiz' | 'ai_instant_win' | 'ai_game_contest'): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('increment_ai_usage', {
        p_tenant_id: tenantId,
        p_feature_type: featureType
      });

      if (error) {
        console.error('Error incrementing usage:', error);
        return false;
      }

      return data === true;
    } catch (err) {
      console.error('Error calling increment_ai_usage:', err);
      return false;
    }
  },

  async updateTenantSubscription(
    tenantId: string,
    updates: Partial<TenantSubscription>
  ): Promise<boolean> {
    const { error } = await supabase
      .from('tenants')
      .update(updates)
      .eq('id', tenantId);

    if (error) {
      console.error('Error updating tenant subscription:', error);
      return false;
    }

    return true;
  },

  hasFeatureAccess(planId: SubscriptionPlan, feature: string): boolean {
    const featureMap: Record<SubscriptionPlan, Record<string, boolean>> = {
      free: {
        quiz_templates: true,
        galaxy_games: true,
        instant_win_templates: false,
        basic_analytics: true,
        ai_quiz_generation: false,
        ai_instant_win: false,
        ai_game_contest: false,
        challenges: false,
        custom_events: false,
      },
      premium: {
        quiz_templates: true,
        galaxy_games: true,
        instant_win_templates: false,
        basic_analytics: true,
        advanced_analytics: true,
        ai_quiz_generation: true,
        ai_instant_win: true,
        ai_game_contest: true,
        challenges: false,
        custom_events: false,
        custom_branding: true,
        api_access: true,
      },
      pro: {
        quiz_templates: true,
        galaxy_games: true,
        instant_win_templates: false,
        basic_analytics: true,
        advanced_analytics: true,
        premium_analytics: true,
        ai_quiz_generation: true,
        ai_instant_win: true,
        ai_game_contest: true,
        challenges: true,
        custom_events: true,
        custom_branding: true,
        white_label: true,
        api_access: true,
        webhook_integration: true,
      }
    };

    return featureMap[planId]?.[feature] || false;
  },

  async getProjectAddons(tenantId: string): Promise<ProjectAddons> {
    try {
      const { data, error } = await supabase.rpc('get_project_addons', {
        p_tenant_id: tenantId
      });

      if (error) {
        console.error('Error fetching project addons:', error);
        return {
          has_addons: false,
          quantity: 0,
          price_per_slot: 5.00,
          monthly_cost: 0
        };
      }

      return data;
    } catch (err) {
      console.error('Error calling get_project_addons:', err);
      return {
        has_addons: false,
        quantity: 0,
        price_per_slot: 5.00,
        monthly_cost: 0
      };
    }
  },

  async purchaseProjectAddon(tenantId: string, quantity: number): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: existing, error: checkError } = await supabase
        .from('project_addons')
        .select('id, quantity')
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (checkError) {
        return { success: false, error: checkError.message };
      }

      if (existing) {
        const { error: updateError } = await supabase
          .from('project_addons')
          .update({ quantity: existing.quantity + quantity, is_active: true })
          .eq('id', existing.id);

        if (updateError) {
          return { success: false, error: updateError.message };
        }
      } else {
        const { error: insertError } = await supabase
          .from('project_addons')
          .insert({
            tenant_id: tenantId,
            quantity,
            price_per_slot: 5.00,
            is_active: true
          });

        if (insertError) {
          return { success: false, error: insertError.message };
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Error purchasing project addon:', err);
      return { success: false, error: 'Failed to purchase addon' };
    }
  },

  async updateProjectAddonQuantity(tenantId: string, newQuantity: number): Promise<{ success: boolean; error?: string }> {
    try {
      if (newQuantity <= 0) {
        const { error } = await supabase
          .from('project_addons')
          .update({ quantity: 0, is_active: false })
          .eq('tenant_id', tenantId);

        if (error) {
          return { success: false, error: error.message };
        }
      } else {
        const { data: existing } = await supabase
          .from('project_addons')
          .select('id')
          .eq('tenant_id', tenantId)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from('project_addons')
            .update({ quantity: newQuantity, is_active: true })
            .eq('tenant_id', tenantId);

          if (error) {
            return { success: false, error: error.message };
          }
        } else {
          const { error } = await supabase
            .from('project_addons')
            .insert({
              tenant_id: tenantId,
              quantity: newQuantity,
              price_per_slot: 5.00,
              is_active: true
            });

          if (error) {
            return { success: false, error: error.message };
          }
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Error updating project addon:', err);
      return { success: false, error: 'Failed to update addon' };
    }
  },

  canPurchaseProjectAddons(planId: SubscriptionPlan): boolean {
    return planId === 'premium' || planId === 'pro';
  }
};
